import { AxiosRequestConfig } from 'axios'
import hash from 'object-hash'

import { Palette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

interface TopBottomPoint {
  ticker: string
  value: number
}

interface CustomColumnPoint {
  last_value: number
  mean: number
  mean_minus_std: number
  mean_plus_std: number
  multiple: string
  sector: string
}

export interface CustomColumnChartData {
  data: CustomColumnPoint[]
}

export interface TopBottomChartData extends CompassAPIDataBase {
  data: {
    bottom: TopBottomPoint[]
    top: TopBottomPoint[]
  }
  title: string
}

interface QuarterlyColumnPoint {
  [key: string]: any
}

export interface QuarterlyColumnChartData {
  data: QuarterlyColumnPoint[]
}

export namespace StrategyPresentation {
  export namespace Valuation {
    const CONTEXT_ID = 'equity:strategy_presentation:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_ibov_ex_egx_data'
        | 'pe_ibov_domesticos'
        | 'pe_ibov_commodities_ex_egx'
        | 'pe_ibov_domesticos_ex_banks'
        | 'pe_ibov_banks'
        | 'pe_ratio_ibov_domesticos_vs_spy'
        | 'pe_expanded_ibov_domestic_cyclical'
        | 'pe_expanded_ibov_domestic_defensive'
        | 'pe_expanded_ibov_tech'
        | 'ev_ebitda_expanded_ibov_commodities'
        | 'expanded_ibov_z_score_regions'
        | 'expanded_ibov_mixed_z_score_median'
        | 'pe_per_sector'
        | 'ev_ebitda_per_sector'
        | 'ke'
        | 'erp_ibov_ex_ogx'
        | 'erp_ex_commodities'
        | 'pe_spy'
        | 'pe_spy_consumer_discretionary_information_technology'
        | 'pe_spy_ex_it_and_faang'
        | 'pe_ratio_spy_vs_spy_ex_it_and_faang'
        | 'pe_ratio_spy_vs_ibov_ex_gx'
      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CustomColumnChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ke':
          case 'pe_spy_consumer_discretionary_information_technology':
            return getMultiSeriesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'expanded_ibov_z_score_regions':
            return getStackedChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'pe_ratio_ibov_domesticos_vs_spy':
          case 'pe_ratio_spy_vs_spy_ex_it_and_faang':
          case 'pe_ratio_spy_vs_ibov_ex_gx':
            return getPeRatioChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'pe_per_sector':
          case 'ev_ebitda_per_sector':
            return getColumnChartProps(config, data as CustomColumnChartData)
          default:
            return getMSDMinMaxChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const isPercent = (chart: ChartConfig['chart']) =>
      chart === 'ke' ||
      chart === 'erp_ibov_ex_ogx' ||
      chart === 'erp_ex_commodities'

    const CHART_TITLES: Record<ChartConfig['chart'], string> = {
      pe_ibov_ex_egx_data: 'P/E 12m - IBOV ex-OGX',
      pe_ibov_domesticos: 'P/E 12m - IBOV domésticos',
      pe_ibov_commodities_ex_egx: 'P/E 12m - IBOV commodities ex-OGX',
      pe_ibov_domesticos_ex_banks: 'P/E 12m - IBOV doméstico ex-Banks',
      pe_ibov_banks: 'P/E 12m - IBOV Banks',
      pe_ratio_ibov_domesticos_vs_spy: 'Ratio P/E 12m - IBOV domésticos / S&P',
      pe_expanded_ibov_domestic_cyclical:
        'P/E expanded IBOV - Domestic cyclical',
      pe_expanded_ibov_domestic_defensive:
        'P/E expanded IBOV - Domestic defensive',
      pe_expanded_ibov_tech: 'P/E expanded IBOV - Tech',
      ev_ebitda_expanded_ibov_commodities: 'Commodities EV/EBITDA',
      expanded_ibov_z_score_regions: 'Z-score regions - Expanded IBOV',
      expanded_ibov_mixed_z_score_median: 'Z-score expanded IBOV - Mediana',
      pe_per_sector: 'PE por setor - Expanded IBOV',
      ev_ebitda_per_sector: 'EV/EBITDA por setor - Expanded IBOV',
      ke: 'Ke',
      erp_ibov_ex_ogx: 'ERP simplificado IBOV ex-OGX',
      erp_ex_commodities: 'ERP simplificado IBOV ex-Commodities',
      pe_spy: 'P/E 12m: S&P',
      pe_spy_consumer_discretionary_information_technology: 'Setores S&P',
      pe_spy_ex_it_and_faang: 'P/E 12m: S&P - Tech & FAANG',
      pe_ratio_spy_vs_spy_ex_it_and_faang: 'Ratio P/E: S&P - Tech & FAANG',
      pe_ratio_spy_vs_ibov_ex_gx: 'Ratio P/E: S&P / IBOV',
    }

    const getColumnChartProps = (
      config: ChartConfig,
      data: CustomColumnChartData
    ) => {
      const series = [
        'last_value',
        'mean',
        'mean_minus_std',
        'mean_plus_std',
      ].map((key, idx) => ({
        id: `series-${idx}`,
        type:
          key === 'mean' || key === 'mean_minus_std' || key === 'mean_plus_std'
            ? 'line'
            : 'column',
        name: capitalizeString(key).replaceAll('_', ' '),
        data: data.data.map((point: CustomColumnPoint) => [
          key,
          point[key as keyof CustomColumnPoint],
        ]),
        dataLabels: { enabled: true, format: '{y:.2f}' },

        ...(key === 'mean' ||
        key === 'mean_minus_std' ||
        key === 'mean_plus_std'
          ? {
              lineWidth: 0,
              marker: { enabled: true, radius: 2 },
              states: { hover: { lineWidthPlus: 0 } },
            }
          : {}),
        showInLegend: false,
        yAxis: 'default',
      }))

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [{ categories: data.data.map(({ sector }) => sector) }]

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          ...(config.chart === 'pe_per_sector' ? { max: 50, min: 0 } : {}),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: {
          ...commonChartOptions,
          credits: { text: 'Fonte: JGP' },
          series,
          stockTools: { gui: { enabled: false } },
          tooltip,
          xAxis,
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getMultiSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(isPercent(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          addMinMaxIndicators: true,
          chartTitle: CHART_TITLES[config.chart],
        },
        {
          name:
            config.chart === 'ke'
              ? ['Foreigner KE', 'Local KE', 'Average KE']
              : ['Information Technology', 'Consumer Discretionary'],
        }
      )(config, data)

    const getStackedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          chartTitle: CHART_TITLES[config.chart],
          plotOptions: { area: { stacking: 'normal' } },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    const getMSDMinMaxChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(isPercent(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          addMinMaxIndicators: true,
          chartTitle: CHART_TITLES[config.chart],
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getPeRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: CHART_TITLES[config.chart],
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name:
            config.chart === 'pe_ratio_ibov_domesticos_vs_spy'
              ? ['Ratio', 'Domestic IBOV', 'SPY']
              : config.chart === 'pe_ratio_spy_vs_spy_ex_it_and_faang'
              ? ['Ratio', 'SPY', 'SPY - Tech & FAANG']
              : ['Ratio', 'SPY', 'IBOV ex-OGX'],
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }

  export namespace Performance {
    const CONTEXT_ID = 'equity:strategy_presentation:performance'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ibov_methodologies'
        | 'expanded_ibov_mkt_cap_weighted'
        | 'macro_sectors_top_bottom_commodities'
        | 'macro_sectors_top_bottom_domestic_cyclical'
        | 'macro_sectors_top_bottom_domestic_defensive'
        | 'macro_sectors_top_bottom_tech'
        | 's_and_p_performances_3y'
        | 's_and_p_performances_itd'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData | TopBottomChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ibov_methodologies':
          case 'expanded_ibov_mkt_cap_weighted':
          case 's_and_p_performances_3y':
          case 's_and_p_performances_itd':
            return getMultiSeriesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'macro_sectors_top_bottom_commodities':
          case 'macro_sectors_top_bottom_domestic_cyclical':
          case 'macro_sectors_top_bottom_domestic_defensive':
          case 'macro_sectors_top_bottom_tech':
            return getTopBottomChartProps(config, data as TopBottomChartData)
        }
      },
    }

    const isPercent = (chart: ChartConfig['chart']) =>
      chart === 'ibov_methodologies' ||
      chart === 's_and_p_performances_3y' ||
      chart === 's_and_p_performances_itd'

    const CHART_TITLES: Record<ChartConfig['chart'], string> = {
      ibov_methodologies: 'Ibovespa vs. S&P em BRL',
      expanded_ibov_mkt_cap_weighted:
        'Expanded IBOV Mkt. Cap Weighted - Performances dos Macro setores',
      s_and_p_performances_3y:
        'Performance S&P - Sem FAANG e apenas FAANG (3Y)',
      s_and_p_performances_itd:
        'Performance S&P - Sem FAANG e apenas FAANG (ITD)',
      macro_sectors_top_bottom_commodities: 'Commodities',
      macro_sectors_top_bottom_domestic_cyclical: 'Domestic cyclical',
      macro_sectors_top_bottom_domestic_defensive: 'Domestic defensive',
      macro_sectors_top_bottom_tech: 'Tech',
    }

    const getMultiSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(isPercent(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          addMinMaxIndicators: true,
          chartTitle: CHART_TITLES[config.chart],
        },
        {
          ...(config.chart === 'expanded_ibov_mkt_cap_weighted'
            ? {
                name: [
                  'Commodities',
                  'Domestic cyclical',
                  'Domestic defensive',
                  'Tech',
                ],
              }
            : {}),
          ...(config.chart === 'ibov_methodologies'
            ? { name: ['Ibovespa', 'Ibovespa nova metodologia', 'S&P em BRL'] }
            : {}),
        }
      )(config, data)

    const getTopBottomChartProps = (
      config: ChartConfig,
      data: TopBottomChartData
    ) => {
      const series = [
        {
          data: data
            ? [
                ...data.data.top,
                { ticker: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.ticker,
                y: column.value * 100,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f} %' },
          color: Palette.green,
          showInLegend: false,
        },
      ]

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: '%',
      }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [{ title: { text: '' }, labels: { format: '{value} %' } }]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            chart: { type: 'bar' },
            series,
            yAxis,
            xAxis,
            plotOptions: { column: { stacking: 'normal' } },
            tooltip,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace Earnings {
    const CONTEXT_ID = 'equity:strategy_presentation:earnings'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'earnings_estimates_ibov_ex_ogx'
        | 'earnings_estimates_ibov_domestic'
        | 'earnings_estimates_ibov_commodities'
        | 'earnings_estimates_ibov_banks'
        | 'earnings_estimates_ibo_domestic_ex_banks'
        | 'earnings_surprises_quarterly'
        | 'earnings_surprises_ticker'
        | 'positive_surprises'
        | 'ibov_sell_side_sentiment'
        | 'ibov_sell_side_sentiment_ex_commodities'

      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | TopBottomChartData
      | QuarterlyColumnChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'earnings_estimates_ibov_ex_ogx':
          case 'earnings_estimates_ibov_domestic':
          case 'earnings_estimates_ibov_commodities':
          case 'earnings_estimates_ibov_banks':
          case 'earnings_estimates_ibo_domestic_ex_banks':
            return getMultiSeriesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'earnings_surprises_quarterly':
            return getEarningsSurprisesQuarterlyProps(
              config,
              data as QuarterlyColumnChartData
            )
          case 'earnings_surprises_ticker':
            return getEarningsSurprisesTickerChartProps(
              config,
              data as TopBottomChartData
            )
          case 'positive_surprises':
            return getPositiveSurprisesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'ibov_sell_side_sentiment':
          case 'ibov_sell_side_sentiment_ex_commodities':
            return getSentimentChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const CHART_TITLES: Record<ChartConfig['chart'], string> = {
      earnings_estimates_ibov_ex_ogx: 'Earnings IBOV ex-OGX',
      earnings_estimates_ibov_domestic: 'Earnings IBOV Domestic',
      earnings_estimates_ibov_commodities: 'Earnings IBOV Commodities',
      earnings_estimates_ibov_banks: 'Earnings IBOV Banks',
      earnings_estimates_ibo_domestic_ex_banks:
        'Earnings IBOV Domestic ex-Banks',
      earnings_surprises_quarterly: 'Earnings - IBOV points',
      earnings_surprises_ticker: 'Earnings IBOV surprise - points',
      positive_surprises: 'Positive surprises',
      ibov_sell_side_sentiment: 'IBOV sentiment',
      ibov_sell_side_sentiment_ex_commodities: 'IBOV sentiment ex-Commodities',
    }

    const getMultiSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle: CHART_TITLES[config.chart],
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          xDateFormat: '%B %Y',
        },
      })(config, data)

    const getEarningsSurprisesQuarterlyProps = (
      config: ChartConfig,
      data: QuarterlyColumnChartData
    ) => {
      const series = {
        id: 'series-0',
        type: 'column',
        name: 'Earnings - Pontos do IBOV',
        data: data.data.map((point) => Object.entries(point)),
        showInLegend: false,
        yAxis: 'default',
      }

      console.log(`series`, series)

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [
        { categories: data.data.map((point) => Object.keys(point)).flat() },
      ]

      const yAxis = [{ id: 'default', opposite: false, title: { text: '' } }]

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: {
          ...commonChartOptions,
          credits: { text: 'Fonte: JGP' },
          series,
          stockTools: { gui: { enabled: false } },
          tooltip,
          xAxis,
          yAxis,
        },
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getEarningsSurprisesTickerChartProps = (
      config: ChartConfig,
      data: TopBottomChartData
    ) => {
      const series = [
        {
          data: data
            ? [
                ...data.data.top,
                { ticker: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.ticker,
                y: column.value,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.3f}' },
          color: Palette.green,
          showInLegend: false,
        },
      ]

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [{ title: { text: '' } }]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            chart: { type: 'bar' },
            series,
            yAxis,
            xAxis,
            plotOptions: { column: { stacking: 'normal' } },
            tooltip,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getPositiveSurprisesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: Array(data.data.length).fill(true),
          chartTitle: CHART_TITLES[config.chart],
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          type: Array(data.data.length).fill('spline'),
          name: ['IBOV ex-Commodities', 'IBOV ex-OGX'],
        }
      )(config, data)

    const getSentimentChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: CHART_TITLES[config.chart],
          tooltip: {
            pointFormatter: magnitudePointFormatter({ decimals: 0 }),
            xDateFormat: "Q%q'%y",
          },
          xAxis: {
            labels: {
              format: "{value:Q%q'%y}",
              rotation: -50,
            },
            tickPositions: data.data[0].data.map((point) =>
              new Date(point.date).getTime()
            ),
          },
        },
        {
          type: Array(data.data.length).fill('spline'),
          name: ['Mean', 'Equal Weighted'],
        }
      )(config, data)
  }

  export namespace Technical {
    const CONTEXT_ID = 'equity:strategy_presentation:technical'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rsi' | 'breadth'
      context: typeof CONTEXT_ID
      reqConfig?: Partial<AxiosRequestConfig>
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const getChartData = (config: ChartConfig) =>
      getCompassAPIChartData(config, (conf) =>
        mergeDeepAll([conf, config.reqConfig ?? {}])
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            yAxis:
              config.chart === 'breadth'
                ? [
                    {
                      id: 'primary',
                      opposite: false,
                      startOnTick: false,
                      endOnTick: false,
                    },
                    {
                      id: 'secondary',
                      opposite: true,
                      startOnTick: false,
                      endOnTick: false,
                    },
                  ]
                : undefined,
            addMinMaxIndicators: true,
            chartTitle: config.title,
            msdIndicators: {
              avg: false,
              sd1: false,
              sd2: false,
            },
          },
          {
            yAxis:
              config.chart === 'breadth'
                ? data.data.map((value) =>
                    value.name.toLocaleLowerCase().includes('breadth')
                      ? 'secondary'
                      : 'primary'
                  )
                : undefined,
            name: data.data.map((value) =>
              value.name.toLocaleLowerCase().includes('breadth')
                ? 'Breadth'
                : value.name
            ),
          }
        )(config, data),
    }
  }

  export namespace FluxoPosicionamento {
    const CONTEXT_ID = 'equity:strategy_presentation:fluxo_posicionamento'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ipo_and_follow_on'
        | 'dividends_and_buyback'
        | 'dividends_and_buyback_ex_ipos_and_follow_on'
        | 'weekly_equity_inflow'
        | 'acc_inflow'
        | 'funds_cash_exp'
        | 'insider_trades'
        | 'insider_trading_conselho_and_diretoria'
        | 'ibov_put_call_ratio'
        | 'ibov_open_position'
        | 'put_call_20_first'
        | 'put_call_20_last'
        | 'termo_mkt_cap_ibov'
        | 'termo_quantidade_ibov'
        | 'b3_termo_pct_mkt_cap'
        | 'ibov_short_interest'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ipo_and_follow_on':
          case 'dividends_and_buyback':
          case 'dividends_and_buyback_ex_ipos_and_follow_on':
            return getMultiColumnsChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'weekly_equity_inflow':
          case 'termo_mkt_cap_ibov':
          case 'termo_quantidade_ibov':
          case 'ibov_short_interest':
            return getDoubleYAxisProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'funds_cash_exp':
            return getPercentChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          default:
            return getMultiSeriesChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const CHART_TITLES: Record<ChartConfig['chart'], string> = {
      ipo_and_follow_on: 'BRL - IPO e Follow On - LTM',
      dividends_and_buyback: 'BRL - Dividendos e Buybacks - LTM',
      dividends_and_buyback_ex_ipos_and_follow_on:
        'BRL - Dividendos e Buybacks menos IPOs e Follow-On',
      weekly_equity_inflow:
        'Captação dos fundos de ação (MM - semanal) vs. IBOV',
      acc_inflow: 'Captação acumulada - Fundos de ação (MM)',
      funds_cash_exp: 'Caixa dos Fundos de Ação',
      insider_trades: 'Maiores Compras e Vendas',
      insider_trading_conselho_and_diretoria:
        'IBOV vs. Insider trading (Conselho + Diretoria) Bi',
      ibov_put_call_ratio: 'IBOV - Put/Call ratio',
      ibov_open_position: 'Ibovespa index option structure',
      put_call_20_first: '20 maiores Put/Call ratio',
      put_call_20_last: '20 menores Put/Call ratio',
      termo_mkt_cap_ibov: 'Operação a termo - Financeiro (Bi)',
      termo_quantidade_ibov: 'Operação a termo - Quantidade (Bi)',
      b3_termo_pct_mkt_cap: 'Termo Pct. Mkt. Cap. - 20 maiores papeis',
      ibov_short_interest: 'IBOV - Short interest',
    }

    const getMultiColumnsChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          chartTitle: CHART_TITLES[config.chart],
          plotOptions: { column: { stacking: 'normal' } },
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          name:
            config.chart === 'ipo_and_follow_on'
              ? ['IPO', 'Follow-on']
              : config.chart === 'dividends_and_buyback'
              ? ['Buyback', 'Dividends projection', 'Dividends']
              : [''],
        }
      )(config, data)

    const getDoubleYAxisProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.chart === 'ibov_short_interest' ||
          config.chart === 'termo_mkt_cap_ibov'
            ? { convertToPercentages: [true, false] }
            : {}),
          chartTitle: CHART_TITLES[config.chart],
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: [
            { id: 'primary' },
            {
              id: 'secondary',
              opposite: true,
              ...(config.chart === 'ibov_short_interest'
                ? { labelSuffix: '%' }
                : {}),
            },
          ],
        },
        {
          name:
            config.chart === 'termo_mkt_cap_ibov'
              ? ['Financeiro', 'IBOV']
              : config.chart === 'termo_quantidade_ibov'
              ? ['Quantidade', 'IBOV']
              : config.chart === 'ibov_short_interest'
              ? ['Short ratio', 'IBOV']
              : undefined,
          ...(config.chart === 'weekly_equity_inflow'
            ? { type: ['column', 'line'] }
            : {}),
          yAxis: ['secondary', 'primary'],
        }
      )(config, data)

    const getPercentChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: CHART_TITLES[config.chart],
          convertToPercentages: [true],
          tooltip: { pointFormatter: magnitudePointFormatter() },
        },
        { name: [''] }
      )(config, data)

    const getMultiSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: CHART_TITLES[config.chart],
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
          },
        },
        {
          ...(config.chart === 'weekly_equity_inflow'
            ? { type: ['column', 'line'] }
            : {}),
          ...(data.data.length === 1 ? { name: [''] } : {}),
        }
      )(config, data)
  }
}
